import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import RecordVoiceOverIcon from '@material-ui/icons/RecordVoiceOver';
import CodeIcon from '@material-ui/icons/Code';
import { SvgIconProps } from '@material-ui/core/SvgIcon';

import authStore from '~/stores/authStore';

const useStyles = makeStyles(({ spacing, palette }) =>
    createStyles({
        root: {
            paddingTop: spacing(6),
            paddingBottom: spacing(6)
        },
        step: {
            padding: spacing(3),
            textAlign: 'center',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            '& > * + *': {
                marginTop: spacing(2)
            }
        },
        stepNumber: {
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: palette.secondary.main,
            color: palette.secondary.contrastText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            fontWeight: 'bold',
            flexShrink: 0
        },
        icon: {
            fontSize: 48,
            color: palette.primary.main
        }
    })
);

type Step = { icon: React.ComponentType<SvgIconProps>; title: string; text: string };
type LangEntry = { sectionTitle: string; steps: Step[] };

const langLib: Record<'en' | 'ru', LangEntry> = {
    en: {
        sectionTitle: 'HOW IT WORKS',
        steps: [
            {
                icon: CloudUploadIcon,
                title: 'Upload audio',
                text: 'Send an audio file (MP3, WAV, OGG, M4A and any other format) via a simple HTTP request to the API.'
            },
            {
                icon: RecordVoiceOverIcon,
                title: 'Get the transcript',
                text: 'The API returns accurate text with speaker labels and timestamps for each phrase.'
            },
            {
                icon: CodeIcon,
                title: 'Integrate the result',
                text: 'Use the JSON response in your CRM, analytics system, support platform, or archive.'
            }
        ]
    },
    ru: {
        sectionTitle: 'КАК ЭТО РАБОТАЕТ',
        steps: [
            {
                icon: CloudUploadIcon,
                title: 'Загрузите аудио',
                text: 'Отправьте файл (MP3, WAV, OGG, M4A и любой другой формат) через HTTP запрос к API.'
            },
            {
                icon: RecordVoiceOverIcon,
                title: 'Получите расшифровку',
                text: 'API возвращает точный текст с разметкой по спикерам и временными метками каждой фразы.'
            },
            {
                icon: CodeIcon,
                title: 'Интегрируйте результат',
                text: 'Используйте JSON ответ в вашей CRM, аналитике, платформе поддержки или архиве.'
            }
        ]
    }
};

const HowItWorks = () => {
    const classes = useStyles();
    const { lang } = authStore;
    const lib = langLib[lang];

    return (
        <Container maxWidth="md">
            <div className={classes.root}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Typography variant="h5" component="h2" align="center">
                            {lib['sectionTitle']}
                        </Typography>
                    </Grid>
                    {lib['steps'].map((step, i) => {
                        const Icon = step.icon;
                        return (
                            <Grid item xs={12} md={4} key={i}>
                                <Paper elevation={2} className={classes.step}>
                                    <div className={classes.stepNumber}>{i + 1}</div>
                                    <Icon className={classes.icon} />
                                    <Typography variant="h6">{step.title}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {step.text}
                                    </Typography>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </div>
        </Container>
    );
};

export default observer(HowItWorks);
